import React, { useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { usePageHeader } from '../../../contexts/PageHeaderContext'
import Icon from '../../../components/Icon'
import { Button, Select, TableShell, UnderlineTabs } from '../../../components/ui'
import { isArtel, isHired } from './incomeReportCalculations'
import {
  ConfigMoneyField,
  ConfigSelectField,
  ConfigTextField,
  MoneyInput,
  ReportErrorState,
  ReportLoadingState,
  ReportPageShell,
  formatMoney,
  makeClientId,
  reportTitle,
  toNumber,
} from './IncomeReportComponents'
import './Reports.css'
import useIncomeReportEditor from './useIncomeReportEditor'

const CONFIG_TABS = [
  { id: 'hired', label: 'Наемные' },
  { id: 'additional', label: 'Доп. сотрудники' },
  { id: 'extra', label: 'Доп. расходы/доходы' },
  { id: 'main', label: 'Осн. доходы/расходы' },
  { id: 'labor', label: 'Выплата по трудовому' },
]

const ADDITIONAL_EMPLOYEE_TYPE_OPTIONS = ['подрядчик', 'самозанятый', 'наемный', 'другое']

function updateArrayItem(items, id, patch) {
  return (items || []).map((item) => (item.id === id ? { ...item, ...patch } : item))
}

function IncomeReportConfigPage() {
  const { id } = useParams()
  const location = useLocation()
  const {
    payload, sourceRows, config, setConfig, loading, saving, error, message, fetchReport, persistReport,
  } = useIncomeReportEditor(id, { configOnly: true })
  const [activeTab, setActiveTab] = useState('hired')

  const report = payload?.report
  const readOnly = !payload?.can_edit
  const hiredRows = useMemo(() => sourceRows.filter((row) => isHired(row.employee_type)), [sourceRows])
  const artelRows = useMemo(() => sourceRows.filter((row) => isArtel(row.employee_type)), [sourceRows])

  usePageHeader({
    title: report ? `Конфигурация: ${reportTitle(report)}` : 'Конфигурация отчета',
    backLink: `/company/reports/${id}${location.search}`,
    backLabel: '← К отчету',
  }, [id, location.search, report?.id, report?.title])

  const saveConfig = () => persistReport()

  const setHiredSalary = (employee, salary) => {
    setConfig((prev) => {
      const rest = (prev.hired_salaries || []).filter((item) => item.employee_id !== employee.employee_id)
      if (toNumber(salary) === 0) return { ...prev, hired_salaries: rest }
      return {
        ...prev,
        hired_salaries: [
          ...rest,
          {
            employee_id: employee.employee_id,
            employee_name: employee.employee_name,
            team_name: employee.team_name,
            salary,
          },
        ],
      }
    })
  }

  const addAdditionalEmployee = () => {
    setConfig((prev) => ({
      ...prev,
      additional_employees: [
        ...(prev.additional_employees || []),
        { id: makeClientId('additional'), name: '', employee_type: 'подрядчик', payment: 0, salary: 0 },
      ],
    }))
  }

  const addExpense = (category, type) => {
    setConfig((prev) => ({
      ...prev,
      expenses: [
        ...(prev.expenses || []),
        { id: makeClientId('expense'), category, type, title: '', amount: 0, comment: '' },
      ],
    }))
  }

  const addLaborException = () => {
    const used = new Set((config.labor_exceptions || []).map((item) => item.employee_id))
    const employee = artelRows.find((row) => row.employee_id && !used.has(row.employee_id)) || artelRows[0]
    if (!employee) return
    setConfig((prev) => ({
      ...prev,
      labor_exceptions: [
        ...(prev.labor_exceptions || []),
        { id: makeClientId('labor'), employee_id: employee.employee_id, employee_name: employee.employee_name, amount: prev.labor_pay_per_person || 0 },
      ],
    }))
  }

  if (loading) return <ReportLoadingState message="Загрузка конфигурации…" />
  if (error && !payload) return <ReportErrorState message={error} onRetry={fetchReport} />

  const salaryByEmployee = new Map((config.hired_salaries || []).map((item) => [item.employee_id, item.salary]))
  const extraExpenses = (config.expenses || []).filter((item) => item.category === 'additional')
  const mainExpenses = (config.expenses || []).filter((item) => item.category === 'main')

  return (
    <ReportPageShell>
      <div className="report-toolbar report-toolbar--compact">
        <div className="report-toolbar__meta">
          <h1>Конфигурация</h1>
          <span>{report?.period_label || '—'}</span>
        </div>
        {!readOnly && (
          <Button type="button" variant="primary" onClick={saveConfig} disabled={saving}>
            {saving ? 'Сохранение…' : 'Сохранить'}
          </Button>
        )}
      </div>

      {readOnly && <div className="report-readonly-note">Режим просмотра: конфигурация недоступна для редактирования.</div>}
      {message.text && <div className={`message message-${message.type} reports-message`} role="alert">{message.text}</div>}
      {error && <div className="message message-error reports-message">{error}</div>}

      <section className="report-config-card">
        <UnderlineTabs tabs={CONFIG_TABS} activeId={activeTab} onChange={setActiveTab} ariaLabel="Вкладки конфигурации" />

        {activeTab === 'hired' && (
          <TableShell className="report-config-table-shell">
            <table className="report-config-table">
              <thead>
                <tr>
                  <th>ФИО</th>
                  <th>Команда</th>
                  <th className="report-num">Зарплата</th>
                </tr>
              </thead>
              <tbody>
                {hiredRows.length === 0 && <tr><td colSpan={3} className="report-empty-cell">В снимке нет наемных сотрудников</td></tr>}
                {hiredRows.map((employee) => (
                  <tr key={employee.row_key}>
                    <td>{employee.employee_name}</td>
                    <td>{employee.team_name || '—'}</td>
                    <td className="report-num">
                      <MoneyInput
                        value={salaryByEmployee.get(employee.employee_id) || 0}
                        readOnly={readOnly}
                        onChange={(value) => setHiredSalary(employee, value)}
                        ariaLabel={`Зарплата: ${employee.employee_name}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableShell>
        )}

        {activeTab === 'additional' && (
          <div className="report-config-section">
            <EditableRowsHeader title="Дополнительные сотрудники" readOnly={readOnly} onAdd={addAdditionalEmployee} />
            <TableShell className="report-config-table-shell">
              <table className="report-config-table">
                <thead>
                  <tr>
                    <th>ФИО</th>
                    <th>Тип</th>
                    <th className="report-num">Выплата</th>
                    <th className="report-num">Зарплата</th>
                    <th className="report-action-col" />
                  </tr>
                </thead>
                <tbody>
                  {(config.additional_employees || []).length === 0 && <tr><td colSpan={5} className="report-empty-cell">Пусто</td></tr>}
                  {(config.additional_employees || []).map((item) => {
                    const employeeType = item.employee_type || item.type || 'подрядчик'
                    const employeeName = item.name || 'дополнительный сотрудник'
                    return (
                      <tr key={item.id}>
                        <td>
                          <ConfigTextField
                            label="ФИО"
                            value={item.name}
                            readOnly={readOnly}
                            onChange={(name) => setConfig((prev) => ({ ...prev, additional_employees: updateArrayItem(prev.additional_employees, item.id, { name }) }))}
                          />
                        </td>
                        <td>
                          <ConfigSelectField
                            label="Тип"
                            value={employeeType}
                            readOnly={readOnly}
                            onChange={(employee_type) => setConfig((prev) => ({ ...prev, additional_employees: updateArrayItem(prev.additional_employees, item.id, { employee_type }) }))}
                          >
                            {ADDITIONAL_EMPLOYEE_TYPE_OPTIONS.map((type) => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </ConfigSelectField>
                        </td>
                        <td className="report-num">
                          <MoneyInput
                            value={item.payment || 0}
                            readOnly={readOnly}
                            onChange={(payment) => setConfig((prev) => ({ ...prev, additional_employees: updateArrayItem(prev.additional_employees, item.id, { payment }) }))}
                            ariaLabel={`Выплата: ${employeeName}`}
                          />
                        </td>
                        <td className="report-num">
                          <MoneyInput
                            value={isHired(employeeType) ? item.salary || 0 : 0}
                            readOnly={readOnly || !isHired(employeeType)}
                            onChange={(salary) => setConfig((prev) => ({ ...prev, additional_employees: updateArrayItem(prev.additional_employees, item.id, { salary }) }))}
                            ariaLabel={`Зарплата: ${employeeName}`}
                          />
                        </td>
                        <td>
                          {!readOnly && (
                            <Button type="button" variant="ghost" size="sm" onClick={() => setConfig((prev) => ({ ...prev, additional_employees: prev.additional_employees.filter((row) => row.id !== item.id) }))}>
                              <Icon name="delete" size={16} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </TableShell>
          </div>
        )}

        {activeTab === 'extra' && (
          <ExpenseEditor
            title="Дополнительные расходы и доходы"
            rows={extraExpenses}
            readOnly={readOnly}
            onAddIncome={() => addExpense('additional', 'income')}
            onAddExpense={() => addExpense('additional', 'expense')}
            onChange={(rowId, patch) => setConfig((prev) => ({ ...prev, expenses: updateArrayItem(prev.expenses, rowId, patch) }))}
            onDelete={(rowId) => setConfig((prev) => ({ ...prev, expenses: prev.expenses.filter((row) => row.id !== rowId) }))}
          />
        )}

        {activeTab === 'main' && (
          <ExpenseEditor
            title="Основные доходы и расходы для P&L"
            rows={mainExpenses}
            readOnly={readOnly}
            onAddIncome={() => addExpense('main', 'income')}
            onAddExpense={() => addExpense('main', 'expense')}
            onChange={(rowId, patch) => setConfig((prev) => ({ ...prev, expenses: updateArrayItem(prev.expenses, rowId, patch) }))}
            onDelete={(rowId) => setConfig((prev) => ({ ...prev, expenses: prev.expenses.filter((row) => row.id !== rowId) }))}
          />
        )}

        {activeTab === 'labor' && (
          <div className="report-config-section">
            <div className="report-config-grid">
              <ConfigMoneyField
                label="Единая выплата по трудовому"
                value={config.labor_pay_per_person || 0}
                readOnly={readOnly}
                onChange={(value) => setConfig((prev) => ({ ...prev, labor_pay_per_person: value }))}
              />
              <ConfigMoneyField
                label="Фонд содержания (начало)"
                value={config.fund_start || 0}
                readOnly={readOnly}
                onChange={(value) => setConfig((prev) => ({ ...prev, fund_start: value }))}
              />
              <ConfigMoneyField
                label="Доход по актам"
                value={config.income_acts || 0}
                readOnly={readOnly}
                onChange={(value) => setConfig((prev) => ({ ...prev, income_acts: value }))}
              />
              <ConfigMoneyField
                label="Налоги факт"
                value={config.tax_fact || 0}
                readOnly={readOnly}
                onChange={(value) => setConfig((prev) => ({ ...prev, tax_fact: value }))}
              />
              <ConfigMoneyField
                label="Налоги план"
                value={config.tax_plan || 0}
                readOnly={readOnly}
                onChange={(value) => setConfig((prev) => ({ ...prev, tax_plan: value }))}
              />
              <ConfigMoneyField
                label="Другие расходы факт"
                value={config.other_exp_fact || 0}
                readOnly={readOnly}
                onChange={(value) => setConfig((prev) => ({ ...prev, other_exp_fact: value }))}
              />
              <ConfigMoneyField
                label="Другие расходы план"
                value={config.other_exp_plan || 0}
                readOnly={readOnly}
                onChange={(value) => setConfig((prev) => ({ ...prev, other_exp_plan: value }))}
              />
            </div>
            <EditableRowsHeader title="Исключения" readOnly={readOnly} onAdd={addLaborException} />
            <TableShell className="report-config-table-shell">
              <table className="report-config-table">
                <thead>
                  <tr>
                    <th>Артельщик</th>
                    <th className="report-num">Сумма</th>
                    <th className="report-action-col" />
                  </tr>
                </thead>
                <tbody>
                  {(config.labor_exceptions || []).length === 0 && <tr><td colSpan={3} className="report-empty-cell">Исключений нет</td></tr>}
                  {(config.labor_exceptions || []).map((item) => (
                    <tr key={item.id || item.employee_id}>
                      <td>
                        <Select
                          value={item.employee_id || ''}
                          disabled={readOnly}
                          onChange={(event) => {
                            const employee = artelRows.find((row) => row.employee_id === event.target.value)
                            setConfig((prev) => ({
                              ...prev,
                              labor_exceptions: updateArrayItem(prev.labor_exceptions, item.id, {
                                employee_id: event.target.value,
                                employee_name: employee?.employee_name || item.employee_name,
                              }),
                            }))
                          }}
                        >
                          {artelRows.map((row) => (
                            <option key={row.employee_id} value={row.employee_id}>{row.employee_name}</option>
                          ))}
                        </Select>
                      </td>
                      <td className="report-num">
                        <MoneyInput
                          value={item.amount || 0}
                          readOnly={readOnly}
                          onChange={(amount) => setConfig((prev) => ({ ...prev, labor_exceptions: updateArrayItem(prev.labor_exceptions, item.id, { amount }) }))}
                          ariaLabel={`Исключение: ${item.employee_name || item.employee_id}`}
                        />
                      </td>
                      <td>
                        {!readOnly && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => setConfig((prev) => ({ ...prev, labor_exceptions: prev.labor_exceptions.filter((row) => row.id !== item.id) }))}>
                            <Icon name="delete" size={16} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableShell>
          </div>
        )}
      </section>
    </ReportPageShell>
  )
}

function EditableRowsHeader({ title, readOnly, onAdd }) {
  return (
    <div className="report-config-section-head">
      <h2>{title}</h2>
      {!readOnly && (
        <Button type="button" variant="secondary" size="sm" onClick={onAdd}>
          <Icon name="plus" size={14} /> Добавить
        </Button>
      )}
    </div>
  )
}

function ExpenseEditor({ title, rows, readOnly, onAddIncome, onAddExpense, onChange, onDelete }) {
  const income = rows.filter((row) => row.type === 'income').reduce((sum, row) => sum + toNumber(row.amount), 0)
  const expense = rows.filter((row) => row.type === 'expense').reduce((sum, row) => sum + toNumber(row.amount), 0)
  const balance = income - expense

  return (
    <div className="report-config-section">
      <div className="report-config-section-head">
        <h2>{title}</h2>
        {!readOnly && (
          <div className="report-config-actions">
            <Button type="button" variant="secondary" size="sm" onClick={onAddIncome}><Icon name="plus" size={14} /> Доход</Button>
            <Button type="button" variant="secondary" size="sm" onClick={onAddExpense}><Icon name="plus" size={14} /> Расход</Button>
          </div>
        )}
      </div>
      <TableShell className="report-config-table-shell">
        <table className="report-config-table">
          <thead>
            <tr>
              <th>Тип</th>
              <th>Название</th>
              <th className="report-num">Сумма</th>
              <th>Комментарий</th>
              <th className="report-action-col" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="report-empty-cell">Пусто</td></tr>}
            {rows.map((item) => (
              <tr key={item.id}>
                <td>
                  <ConfigSelectField label="Тип" value={item.type} readOnly={readOnly} onChange={(type) => onChange(item.id, { type })}>
                    <option value="income">Доход</option>
                    <option value="expense">Расход</option>
                  </ConfigSelectField>
                </td>
                <td>
                  <ConfigTextField label="Название" value={item.title} readOnly={readOnly} onChange={(title) => onChange(item.id, { title })} />
                </td>
                <td className="report-num">
                  <MoneyInput value={item.amount || 0} readOnly={readOnly} onChange={(amount) => onChange(item.id, { amount })} ariaLabel={`Сумма: ${item.title || 'строка'}`} />
                </td>
                <td>
                  <ConfigTextField label="Комментарий" value={item.comment} readOnly={readOnly} onChange={(comment) => onChange(item.id, { comment })} />
                </td>
                <td>
                  {!readOnly && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => onDelete(item.id)}>
                      <Icon name="delete" size={16} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableShell>
      <div className="report-balance-bar">
        Доходы: <strong>{formatMoney(income)}</strong>
        <span>Расходы: <strong>{formatMoney(expense)}</strong></span>
        <span>Баланс: <strong className={balance >= 0 ? 'report-positive' : 'report-negative'}>{formatMoney(balance)}</strong></span>
      </div>
    </div>
  )
}

export default IncomeReportConfigPage
